import moment from 'moment';
import { Inject } from '@odinjs/odin'

import { Value } from '@philips-internal/oswald-ui-core';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { ButtonAPI, FormAPI, InputAPI, ModalService, TextAPI, ToastService } from '@philips-internal/oswald-ui-components';
import Controller, { OnClick, OnMount, OnValidate } from '@philips-internal/oswald-ui-core/lib/client';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import { ValidateEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { DateFormatterUtil } from '@philips-emr/health-professional-components/src/utils';

import { FragmentNames, OralSupplementsExecuteTimeModalAliases } from '../../../../codegen';
import { ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import { ApplicationRoute } from '../../../../constants/routes';
import timeService from '../../../../services/time-service';
import BaseController from '../../../BaseController';
import MedicationService from '../../services/medication-service';
import TimelineContext, { TimeLineEventMedication } from '../../services/timeline-service';
import OralSupplementsService, { OralSupplementsCreatePrescriptionScheduleChangeRequest } from '../../services/oral-supplements-service';

@Controller({ fragment: FragmentNames.ORAL_SUPPLEMENTS_EXECUTE_TIME_MODAL })
export default class OralSupplementsExecuteTimeModalController extends BaseController {

  @Inject(OralSupplementsExecuteTimeModalAliases.INFORMATION_FORM)
  private oralSupplementsFormApi!: FormAPI;

  @Inject(OralSupplementsExecuteTimeModalAliases.BUTTON_EXECUTE)
  private executeButtonApi!: ButtonAPI;

  @OnMount(OralSupplementsExecuteTimeModalAliases.DATE_HOUR_INPUT)
  handleMountDateHourInput({ target }: EventData<InputAPI>): void {
    timeService.getCurrentDateTime()
      .then(date => target.setValue(date))
      .catch(() => target.setValue(moment().format(ISO_FULL_DATE_FORMAT)))
  }

  @OnMount(OralSupplementsExecuteTimeModalAliases.INFORMATION_FORM)
  handleMountForm({ target }: EventData<FormAPI>): void {
    const { doseQuantity, unitMeasureDoseDescription } = this.context.getBulkData() as Record<string, string>;
    target.setData({
      amountAdministered: String(doseQuantity),
      date: String(moment()),
      unitMeasureDoseDescription: String(unitMeasureDoseDescription),
    } as Record<string, Value>);
  }

  @OnMount([OralSupplementsExecuteTimeModalAliases.TEXT_MEDICATION_DATE, OralSupplementsExecuteTimeModalAliases.TEXT_MEDICATION_INFORMATION])
  handleMountTexts({ target }: EventData<TextAPI>): void {
    const { materialDescription, scheduleDate } = this.context.getBulkData();
    const value = target.getValue()
      .replace(':medication-information', materialDescription as string)
      .replace(':medication-date', DateFormatterUtil.timeStamp(String(scheduleDate)));

    target.setValue(value);
  }

  @OnValidate(OralSupplementsExecuteTimeModalAliases.INFORMATION_FORM)
  handleChangeForm(event: ValidateEvent): void {
    const isFormValid = event.valid;
    this.context.set({ valid: isFormValid });
  }

  @OnMount(OralSupplementsExecuteTimeModalAliases.BUTTON_EXECUTE)
  handleMountButtonExecute(): void {
    this.executeButtonApi.setEnabled(!!this.context.get('valid'));
  }

  @OnClick(OralSupplementsExecuteTimeModalAliases.BUTTON_EXECUTE)
  handleClickExecuteButton(): void {
    const { justification, date, amountAdministered } = this.oralSupplementsFormApi.getRecord();
    const { scheduleSequenceNumber } = this.historyService.getCurrentRouteParams();
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const { intervalCode, prescriptionNumber, informationDescription, sequenceDietNumber, doseQuantity, unitMeasureCode } = this.context.getBulkData();
    const timelineMedication = TimelineContext.get() as TimeLineEventMedication;

    const dateTimeCurrent = moment(String(date)).utc().format(ISO_FULL_DATE_FORMAT);

    const body: OralSupplementsCreatePrescriptionScheduleChangeRequest = {
      amountAdministered: Number(amountAdministered),
      encounterId: Number(encounterId),
      eventDate: dateTimeCurrent,
      intervalCode: String(intervalCode),
      itemCode: String(timelineMedication.itemCode),
      itemDescription: String(timelineMedication.description),
      itemQuantity: Number(doseQuantity),
      itemSequence: Number(sequenceDietNumber),
      justificationDescription: justification ? String(justification) : '',
      observationDescription: informationDescription ? String(informationDescription) : '',
      prescriptionNumber: Number(prescriptionNumber),
      prescriptionsNumber: String(prescriptionNumber),
      scheduleSequence: Number(scheduleSequenceNumber),
      unitMeasureCode: String(unitMeasureCode),
    };

    const scheduleSequence = Number(scheduleSequenceNumber);

    this.isLoading(true);

    OralSupplementsService
      .oralSupplementsCreatePrescriptionScheduleChange(scheduleSequence, body)
      .then(response => {
        this.showToast(response.body?.messageDescriptionResponse || 'tws.emar.administered-item', ToastLevel.SUCCESS, ToastType.BANNER);
        this.isLoading(false);
        this.redirectUserToTimeline();
      })
      .catch(error => {
        this.isLoading(false);
        if (error.body && error.body.errors) {
          error.body.errors.map((message: string) => MedicationService.showToast(message, ToastLevel.ERROR));
        }
      })
  }

  private redirectUserToTimeline(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const redirectRoute = ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION;
    applicationContext.redirect(redirectRoute, { encounterId, patientId });
    applicationContext.clearNavigationStack();
    ModalService.close(FragmentNames.ORAL_SUPPLEMENTS_EXECUTE_TIME_MODAL);
  }

  private showToast(message: string, toastLevel: ToastLevel, type: ToastType): void {
    const toastId = 'oral-supplements-execute-time-toast';
    ToastService.close(toastId);
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: toastId,
      level: toastLevel,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type,
    });
  }

  private isLoading(state: boolean): void {
    this.executeButtonApi.setEnabled(!state);
    this.oralSupplementsFormApi.setLoading(state);
  }
}

